import { DeleteOutlined } from "@ant-design/icons";
import { Form, Select } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { orgPermissionEnum } from "@skydio/api_util/src/backends/cloud_api/organizations/utils";
import enums_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/enums_pb";
import { Button } from "@skydio/rivet-ui";

import { useFeatureFlag } from "hooks/useFeatureFlag";
import {
  getOrgRoleDescriptionFromValue,
  getReadableOrgRoleFromValue,
  getReadableOrgRoles,
  getResourceGroupDisplayName,
} from "utils/team";

import type { FormInstance } from "antd";
import type { ValueType } from "@skydio/core";
import type { ResourceGroup } from "./useOrganizationResourceGroups";

interface RoleGroupFormSectionProps {
  form: FormInstance<any>;
  orgName: string;
  resourceGroups: ResourceGroup[];
  validateOrgRole: (_: unknown, role: string) => Promise<void>;
  convertReadableOrgRole: (value: string) => ValueType<enums_pb.OrgPermission.PermEnumMap>;
  showPermissionSummary?: boolean;
  showSkydioRoles?: boolean;
}

interface PermissionSummary {
  role: string;
  description: string;
}

const DefaultRoleWithResourceGroup = {
  rgRole: getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MODERATOR),
  resourceGroupNames: [],
};

const MAX_FIELD_NAMES = 4;

export type RoleWithResourceGroups = {
  rgRole: string;
  resourceGroupNames: string[];
};

const RoleGroupFormSection = ({
  form,
  orgName,
  resourceGroups,
  validateOrgRole,
  convertReadableOrgRole,
  showPermissionSummary = false,
  showSkydioRoles = false,
}: RoleGroupFormSectionProps) => {
  const { t } = useTranslation("user");

  const missionsPageEnabled = useFeatureFlag("missionsPage");
  const vehicleTeleopEnabled = useFeatureFlag("vehicleTeleoperation");
  const showRemotePilotRole = missionsPageEnabled || vehicleTeleopEnabled;
  const supportRolesEnabled = useFeatureFlag("supportUserExpiry");
  const readableOrgRoles = getReadableOrgRoles(showRemotePilotRole, supportRolesEnabled);

  const [permissionSummaries, setPermissionSummaries] = useState<PermissionSummary[]>([]);

  const formRoleWithRgsValue: RoleWithResourceGroups[] =
    form.getFieldValue("roleWithResourceGroups");

  // update the permission summary section based on the selected roles
  useEffect(() => {
    // don't show permission summary if the form value is identical to the default value
    if (
      formRoleWithRgsValue.length === 1 &&
      formRoleWithRgsValue[0].rgRole === DefaultRoleWithResourceGroup.rgRole &&
      formRoleWithRgsValue[0].resourceGroupNames.length === 0
    ) {
      setPermissionSummaries([]);
      return;
    }

    // avoid duplicate role descriptions
    const uniqueRoles = Array.from(
      new Set(formRoleWithRgsValue.map(roleWithRg => roleWithRg.rgRole))
    );

    // collect the permission summary for each role
    const summaries = uniqueRoles
      .filter(role =>
        supportRolesEnabled ? true : role !== "SKYDIO_SUPERUSER" && role !== "SKYDIO_READONLY"
      )
      .map(role => {
        let displayName = role;
        if (role === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN)) {
          displayName = "Organization Admin";
        } else if (
          role === getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MODERATOR)
        ) {
          displayName = "Cloud User";
        }
        return {
          role: displayName,
          description: getOrgRoleDescriptionFromValue(convertReadableOrgRole(role)),
        };
      });

    setPermissionSummaries(summaries);
  }, [formRoleWithRgsValue, orgName, convertReadableOrgRole, supportRolesEnabled]);

  const orgRoleValidators = useMemo(
    () => [
      {
        validator: validateOrgRole,
      },
    ],
    [validateOrgRole]
  );

  // TODO squash down warnings
  return (
    <>
      <div className="mb-2 flex">
        <div className="grow pr-1 font-semibold text-legacyGray-900">Role</div>
        <div className="grow pr-12 font-semibold text-legacyGray-900">Group</div>
      </div>
      {/* Section to select user roles & their respective resource groups */}
      <Form.List name="roleWithResourceGroups">
        {(fields, { add, remove }) => (
          <div>
            {fields.map(field => (
              <div key={field.key} className="flex items-start gap-2">
                <Form.Item
                  {...field}
                  key={"rgRole"}
                  name={[field.name, "rgRole"]}
                  rules={orgRoleValidators}
                >
                  <Select
                    placeholder={t("inviteModal.orgRolePlaceholder")}
                    dropdownMatchSelectWidth={false}
                    className="!w-[200px]"
                    onChange={value => {
                      // clear the adjacent resource group select field when the role is changed to ADMIN or PILOT
                      if (
                        value ===
                          getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN) ||
                        value ===
                          getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER)
                      ) {
                        const updatedRoleWithResourceGroups = form
                          .getFieldValue("roleWithResourceGroups")
                          .map((roleWithRg: RoleWithResourceGroups, index: number) => {
                            if (index === field.name) {
                              return {
                                ...roleWithRg,
                                resourceGroupNames: [],
                              };
                            }
                            return roleWithRg;
                          });

                        form.setFieldsValue({
                          roleWithResourceGroups: updatedRoleWithResourceGroups,
                        });
                      }
                    }}
                  >
                    <Select.OptGroup label="Cloud Roles">
                      {readableOrgRoles
                        .filter(
                          orgRole =>
                            orgRole.enumValue !== orgPermissionEnum.SKYDIO_READONLY &&
                            orgRole.enumValue !== orgPermissionEnum.SKYDIO_SUPERUSER
                        )
                        .map(({ readableName }) => {
                          let displayName = readableName;
                          if (readableName === "Admin") {
                            displayName = "Organization Admin";
                          } else if (readableName === "User") {
                            displayName = "Cloud User";
                          }
                          return (
                            <Select.Option key={readableName} value={readableName}>
                              {displayName}
                            </Select.Option>
                          );
                        })}
                    </Select.OptGroup>
                    {supportRolesEnabled && showSkydioRoles && (
                      <Select.OptGroup label="Skydio Roles">
                        {readableOrgRoles
                          .filter(
                            orgRole =>
                              orgRole.enumValue === orgPermissionEnum.SKYDIO_READONLY ||
                              orgRole.enumValue === orgPermissionEnum.SKYDIO_SUPERUSER
                          )
                          .map(({ readableName }) => {
                            return (
                              <Select.Option key={readableName} value={readableName}>
                                {readableName}
                              </Select.Option>
                            );
                          })}
                      </Select.OptGroup>
                    )}
                  </Select>
                </Form.Item>
                <Form.Item
                  {...field}
                  key={"resourceGroupNames"}
                  name={[field.name, "resourceGroupNames"]}
                  dependencies={[["roleWithResourceGroups", field.name, "resourceGroupNames"]]}
                  rules={[
                    {
                      required:
                        form.getFieldValue(["roleWithResourceGroups", field.name, "rgRole"]) ===
                          getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MODERATOR) ||
                        form.getFieldValue(["roleWithResourceGroups", field.name, "rgRole"]) ===
                          getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.REMOTE_PILOT),
                      message: "Please select at least one group",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    className="!w-[220px]"
                    placeholder={
                      form.getFieldValue(["roleWithResourceGroups", field.name, "rgRole"]) ===
                      getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MEMBER)
                        ? "No Cloud Access"
                        : form.getFieldValue(["roleWithResourceGroups", field.name, "rgRole"]) ===
                              getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.ADMIN) ||
                            form.getFieldValue(["roleWithResourceGroups", field.name, "rgRole"]) ===
                              getReadableOrgRoleFromValue(
                                enums_pb.OrgPermission.PermEnum.SKYDIO_SUPERUSER
                              ) ||
                            form.getFieldValue(["roleWithResourceGroups", field.name, "rgRole"]) ===
                              getReadableOrgRoleFromValue(
                                enums_pb.OrgPermission.PermEnum.SKYDIO_READONLY
                              )
                          ? "Entire Organization"
                          : "Select groups"
                    }
                    disabled={
                      ![
                        getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.MODERATOR),
                        getReadableOrgRoleFromValue(enums_pb.OrgPermission.PermEnum.REMOTE_PILOT),
                      ].includes(
                        form.getFieldValue(["roleWithResourceGroups", field.name, "rgRole"])
                      )
                    }
                  >
                    {resourceGroups.map(({ name }) => (
                      <Select.Option key={name} value={name}>
                        {getResourceGroupDisplayName(orgName, name)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                {/* don't show delete button for first row */}
                {field.name > 0 && (
                  <Form.Item>
                    <DeleteOutlined
                      // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                      onClick={() => remove(field.name)}
                      className="ml-4"
                      style={{ color: "#999999", fontSize: 16 }}
                    />
                  </Form.Item>
                )}
              </div>
            ))}
            {/* disable once 4 rows reaches */}
            <Form.Item>
              <Button
                onPress={() => add(DefaultRoleWithResourceGroup)}
                isDisabled={fields.length >= MAX_FIELD_NAMES}
              >
                Add Role
              </Button>
            </Form.Item>
          </div>
        )}
      </Form.List>
      {/* Permission Summary Section */}
      {showPermissionSummary && permissionSummaries.length > 0 && (
        <div className="my-4">
          <div className="mb-2 font-semibold text-legacyGray-900">Summary of Permissions</div>
          <div className="flex flex-col">
            <ul className="px-4">
              {permissionSummaries.map(({ role, description }, index) => (
                <div key={index} className="mb-2 flex">
                  <li className="text-legacyGray-600">
                    {role}&nbsp;{description}
                  </li>
                </div>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default RoleGroupFormSection;
